import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ViewChild } from '@angular/core';
import { APPLICATION_STATUS, LEAD_BASED_PAINT, STUDENT_CODE_TYPES } from '@shared/utils/app-static-data';
import { IIndividualApplication } from 'app/models/individuals/individual-application';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { IndividualService } from 'app/services/individuals/individual.service';
import { ToastrService } from 'ngx-toastr';
import { IndividualApplicationComponent } from '../individual-application/individual-application.component';

@Component({
  selector: 'app-individual-application-list',
  templateUrl: './individual-application-list.component.html',
  styleUrls: ['./individual-application-list.component.scss']
})
export class IndividualApplicationListComponent implements OnInit, OnChanges {
  @Input() personId: number | null = null;
  @Input() isEmbedded = false;
  @Output() countChanged = new EventEmitter<number>();

  public isMobile = false;
  public loading = true;
  public pageSize = 50;
  public uiData = LEAD_BASED_PAINT;
  public applications: IIndividualApplication[] = [];
  public filteredApplications: IIndividualApplication[] = [];
  public dataSource = new MatTableDataSource<IIndividualApplication>([]);
  public filterString = '';
  public selectedStatus = new FormControl([]);
  public selectedType = new FormControl([]);
  public applicationDateFilterStart = new FormControl(null);
  public applicationDateFilterEnd = new FormControl(null);
  public individualApplicationTable = {
    icon: 'assignment',
    title: 'Individual Applications',
    layout: {
      columns: ['applicationType', 'applicantName', 'status', 'comments', 'applicationDate', 'approvalDate'],
      container: [
        { displayName: 'Application Type', columnName: 'applicationType', type: 'string', size: '16' },
        { displayName: 'Applicant', columnName: 'applicantName', type: 'string', size: '23' },
        { displayName: 'Status', columnName: 'status', type: 'status', size: '11' },
        { displayName: 'Comments', columnName: 'comments', type: 'string', size: '18' },
        { displayName: 'Application Date', columnName: 'applicationDate', type: 'date', size: '11' },
        { displayName: 'Status Date', columnName: 'approvalDate', type: 'date', size: '11' },
      ],
    },
  };

  @ViewChild('TablePaginator') paginator!: MatPaginator;
  @ViewChild('TableSort') sort!: MatSort;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private commonService: CommonService,
    private individualService: IndividualService,
    public sharedService: SharedService,
    private toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['personId'] && !changes['personId'].firstChange) {
      this.loadData();
    }
  }

  get tableColumns(): string[] {
    return this.individualApplicationTable.layout.columns.concat('actions');
  }

  loadData(): void {
    this.loading = true;

    if (!this.isEmbedded) {
      this.filterString = this.commonService.individualApplicationFilter.filterString;
      this.selectedStatus = this.commonService.individualApplicationFilter.selectedStatus;
      this.selectedType = this.commonService.individualApplicationFilter.selectedType;
      this.applicationDateFilterStart.setValue(this.commonService.individualApplicationFilter.applicationDateFilterStart);
      this.applicationDateFilterEnd.setValue(this.commonService.individualApplicationFilter.applicationDateFilterEnd);
    }

    this.individualService.getIndividualApplications(this.personId).subscribe({
      next: (applications) => {
        this.applications = applications;
        this.loading = false;
        this.filterTable();
      },
      error: (error) => {
        this.toastr.error('Error loading Individual applications: ' + error.message);
        this.loading = false;
      }
    });
  }

  filterTable(): void {
    if (!this.isEmbedded) {
      this.commonService.individualApplicationFilter.filterString = this.filterString;
      this.commonService.individualApplicationFilter.selectedStatus = this.selectedStatus;
      this.commonService.individualApplicationFilter.selectedType = this.selectedType;
      this.commonService.individualApplicationFilter.applicationDateFilterStart = this.applicationDateFilterStart.value;
      this.commonService.individualApplicationFilter.applicationDateFilterEnd = this.applicationDateFilterEnd.value;
    }

    this.filteredApplications = this.applications.filter((application) => {
      const text = this.filterString.trim().toLowerCase();
      const matchesText = !text || [
        application.id.toString(),
        application.firstName,
        application.lastName,
        application.userID || '',
        this.getCommentsText(application),
      ].join(' ').toLowerCase().includes(text);

      const statusValues = this.selectedStatus.value || [];
      const matchesStatus = !statusValues.length || statusValues.includes(this.getDisplayStatus(application));

      const typeValues = this.selectedType.value || [];
      const matchesType = !typeValues.length || typeValues.includes(this.getApplicationTypeLabel(application.certificateType, application));

      const applicationDate = application.applicationDate ? this.toDateOnly(application.applicationDate) : null;
      const filterStart = this.applicationDateFilterStart.value ? this.toDateOnly(this.applicationDateFilterStart.value) : null;
      const filterEnd = this.applicationDateFilterEnd.value ? this.toDateOnly(this.applicationDateFilterEnd.value) : null;
      const matchesDate = (!filterStart && !filterEnd)
        || (!!applicationDate
          && (!filterStart || applicationDate >= filterStart)
          && (!filterEnd || applicationDate <= filterEnd));

      return matchesText && matchesStatus && matchesType && matchesDate;
    });

    this.dataSource = new MatTableDataSource<IIndividualApplication>(this.filteredApplications);
    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    });

    this.countChanged.emit(this.filteredApplications.length);
  }

  clearFilters(): void {
    this.filterString = '';
    this.selectedStatus.setValue(['In Progress', 'Submitted', 'In Review', 'Action Needed', 'Expired']);
    this.selectedType.setValue([]);
    this.applicationDateFilterStart.reset();
    this.applicationDateFilterEnd.reset();
    this.filterTable();
  }

  getStatusValues(): string[] {
    return Object.values(APPLICATION_STATUS).concat('Expired');
  }

  getApplicationTypeValues(): string[] {
    return Object.values(STUDENT_CODE_TYPES).sort().concat('Account Link');
  }

  getApplicationTypeLabel(applicationType: string | null, application?: IIndividualApplication): string {
    const value = (applicationType || '').trim();
    if (value) {
      return value;
    }

    if (application?.person?.id) {
      return 'Account Link';
    }

    return 'Not provided';
  }

  getApplicantName(application: IIndividualApplication): string {
    return [application.firstName, application.lastName].filter(Boolean).join(' ') || 'Not provided';
  }

  getColumnText(application: IIndividualApplication, columnName: string): string {
    switch (columnName) {
      case 'applicationType':
        return this.getApplicationTypeLabel(application.certificateType, application);
      case 'applicantName':
        return this.getApplicantName(application);
      case 'comments':
        return this.getCommentsText(application);
      default: {
        const value = application[columnName as keyof IIndividualApplication];
        return value === null || value === undefined || value === '' ? 'Not provided' : value.toString();
      }
    }
  }

  openStartApplication(): void {
    if (this.isEmbedded && this.personId) {
      this.openApplicationDialog({
        personId: this.personId,
        workflow: 'new',
      });
      return;
    }

    this.openApplicationDialog({});
  }

  openApplication(application: IIndividualApplication): void {
    this.openApplicationDialog({ applicationId: application.id });
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  isReadOnlyApplication(application: IIndividualApplication): boolean {
    return !['In Progress', 'Action Needed'].includes(this.getDisplayStatus(application));
  }

  getDisplayStatus(application: IIndividualApplication): string {
    return this.isApplicationExpired(application) ? 'Expired' : application.status;
  }

  getDateColumnValue(application: IIndividualApplication, columnName: string): string | null {
    if (columnName === 'approvalDate') {
      return this.getStatusDate(application);
    }

    const value = application[columnName as keyof IIndividualApplication];
    return typeof value === 'string' ? value : value ? value.toString() : null;
  }

  isCommentsColumn(columnName: string): boolean {
    return columnName === 'comments';
  }

  private getCommentsText(application: IIndividualApplication): string {
    if (!this.individualService.shouldDisplayApplicationDeficiencies(application)) {
      return '';
    }

    return this.individualService.getUnresolvedApplicationDeficiencyText(application);
  }

  private getStatusDate(application: IIndividualApplication): string | null {
    const status = this.normalizeStatus(application.status);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return application.approvalDate || null;
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return application.lastUpdated || null;
    }

    return null;
  }

  private isApplicationExpired(application: IIndividualApplication): boolean {
    if (!application.applicationDate) {
      return false;
    }

    const status = this.normalizeStatus(application.status);
    if (status === this.normalizeStatus(APPLICATION_STATUS.approved) || status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return false;
    }

    const currentDate = this.toDateOnly(new Date());
    const applicationDate = this.toDateOnly(application.applicationDate);
    const diffInDays = (currentDate.getTime() - applicationDate.getTime()) / (1000 * 3600 * 24);
    return diffInDays > 90;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private openApplicationDialog(data: { applicationId?: number; personId?: number; workflow?: 'new' | 'link' }): void {
    const dialogRef = this.dialog.open(IndividualApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
      }
    });
  }

  private toDateOnly(value: string | Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}